import fs from 'fs-extra'
import path from 'path'
import { Logger } from '../../utils/logger'
import { legalizeWeappCss } from '../../utils/weapp-postcss-legalizer'
import { escapeWeappClass } from '../../utils/escape-weapp-class'

interface PatchOptions {
  platform: 'weapp' | 'h5'
  dist: string
  verbose: boolean
  runtime: boolean
  classPatch: boolean
  selectorPatch: boolean
}

export async function patch(options: PatchOptions): Promise<void> {
  const logger = new Logger(options.verbose)
  const { platform, dist, runtime, classPatch, selectorPatch } = options

  logger.success('🚀 开始 patch 构建产物...')
  logger.info('📱 目标平台:', platform)
  logger.info('📁 构建目录:', dist)

  if (!fs.existsSync(dist)) {
    throw new Error(`构建目录不存在: ${dist}`)
  }

  const startTime = Date.now()
  let processedFiles = 0
  let totalSize = 0
  let originalSize = 0

  // 处理 CSS/WXSS 文件
  if (platform === 'weapp') {
    await processWxssFiles(dist, logger, {
      processedFiles,
      totalSize,
      originalSize
    })
  }

  // 处理 JS/TS 文件中的类名
  if (classPatch) {
    await processClassNames(dist, logger)
  }

  // 处理选择器兼容性
  if (selectorPatch) {
    await processSelectors(dist, logger)
  }

  // 添加运行时调试支持
  if (runtime) {
    await addRuntimeSupport(dist, logger)
  }

  const totalTime = Date.now() - startTime
  logger.success('🎉 Patch 完成！')
  logger.info(`⏱️ 总时间: ${totalTime}ms`)
}

async function processWxssFiles(
  distPath: string,
  logger: Logger,
  stats: { processedFiles: number; totalSize: number; originalSize: number }
): Promise<void> {
  const wxssFiles = await findWxssFiles(distPath)

  for (const file of wxssFiles) {
    try {
      const originalCss = await fs.readFile(file, 'utf-8')
      const legalCss = legalizeWeappCss(originalCss)

      await fs.writeFile(file, legalCss)

      stats.processedFiles++
      stats.originalSize += originalCss.length
      stats.totalSize += legalCss.length

      logger.success(`📦 处理文件: ${path.relative(distPath, file)}`)
      logger.info(`📏 原始大小: ${originalCss.length} 字符`)
      logger.info(`📏 处理后: ${legalCss.length} 字符`)
      logger.info(`📈 压缩率: ${((1 - legalCss.length / originalCss.length) * 100).toFixed(2)}%`)
    } catch (error) {
      logger.error(`❌ 处理文件失败: ${file}`, error)
    }
  }
}

async function processClassNames(distPath: string, logger: Logger): Promise<void> {
  const jsFiles = await findJsFiles(distPath)

  for (const file of jsFiles) {
    try {
      let content = await fs.readFile(file, 'utf-8')
      const originalContent = content

      // 修复类名被去除的问题
      content = fixRemovedClassNames(content)

      if (content !== originalContent) {
        await fs.writeFile(file, content)
        logger.success(`🔧 修复类名: ${path.relative(distPath, file)}`)
      }
    } catch (error) {
      logger.error(`❌ 处理类名失败: ${file}`, error)
    }
  }
}

async function processSelectors(distPath: string, logger: Logger): Promise<void> {
  const wxssFiles = await findWxssFiles(distPath)

  for (const file of wxssFiles) {
    try {
      let content = await fs.readFile(file, 'utf-8')
      const originalContent = content

      // 修复选择器兼容性
      content = fixSelectorCompatibility(content)

      if (content !== originalContent) {
        await fs.writeFile(file, content)
        logger.success(`🔧 修复选择器: ${path.relative(distPath, file)}`)
      }
    } catch (error) {
      logger.error(`❌ 处理选择器失败: ${file}`, error)
    }
  }
}

async function addRuntimeSupport(distPath: string, logger: Logger): Promise<void> {
  const runtimeScript = `
// TailwindCSS Taro 运行时支持
(function() {
  // 类名转义函数
  window.escapeWeappClass = function(className) {
    return className.replace(/[^a-zA-Z0-9-_]/g, '_');
  };
  
  // 运行时类名注入
  window.injectTailwindClass = function(element, className) {
    if (element && element.className) {
      const escapedClass = window.escapeWeappClass(className);
      element.className += ' ' + escapedClass;
    }
  };
  
  console.log('🎨 TailwindCSS Taro 运行时支持已加载');
})();
`

  const runtimeFile = path.join(distPath, 'tailwindcss-taro-runtime.js')
  await fs.writeFile(runtimeFile, runtimeScript)
  logger.success('🔧 添加运行时支持: tailwindcss-taro-runtime.js')
}

async function findWxssFiles(dir: string): Promise<string[]> {
  const files: string[] = []
  
  async function scan(currentDir: string) {
    const items = await fs.readdir(currentDir)
    
    for (const item of items) {
      const fullPath = path.join(currentDir, item)
      const stat = await fs.stat(fullPath)
      
      if (stat.isDirectory()) {
        await scan(fullPath)
      } else if (item.endsWith('.wxss')) {
        files.push(fullPath)
      }
    }
  }
  
  await scan(dir)
  return files
}

async function findJsFiles(dir: string): Promise<string[]> {
  const files: string[] = []
  
  async function scan(currentDir: string) {
    const items = await fs.readdir(currentDir)
    
    for (const item of items) {
      const fullPath = path.join(currentDir, item)
      const stat = await fs.stat(fullPath)
      
      if (stat.isDirectory()) {
        await scan(fullPath)
      } else if (item.endsWith('.js') || item.endsWith('.ts')) {
        files.push(fullPath)
      }
    }
  }
  
  await scan(dir)
  return files
}

function fixRemovedClassNames(content: string): string {
  // 修复被去除的类名
  return content
    // 修复 className 属性
    .replace(/className\s*=\s*["']([^"']*)["']/g, (match, className) => {
      const escapedClasses = className.split(/\s+/).map(escapeWeappClass).join(' ')
      return `className="${escapedClasses}"`
    })
    // 修复 class 属性
    .replace(/class\s*=\s*["']([^"']*)["']/g, (match, className) => {
      const escapedClasses = className.split(/\s+/).map(escapeWeappClass).join(' ')
      return `class="${escapedClasses}"`
    })
}

function fixSelectorCompatibility(content: string): string {
  // 修复选择器兼容性
  return content
    // 替换 :root 为 page
    .replace(/:root/g, 'page')
    // 替换 * 为 view,text
    .replace(/\*/g, 'view,text')
    // 移除不兼容的伪类
    .replace(/:[a-z-]+(?=\s*[.{])/g, '')
    // 移除不兼容的伪元素
    .replace(/::[a-z-]+/g, '')
} 